import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Sparkles, ChevronRight } from "lucide-react";
import { API, useApp } from "../context";
import { getActiveLearnerId } from "../hooks/useActiveLearner";

// Personal premium-preview countdown. Shows for a signed-in account that has
// its OWN time-limited preview grant running (e.g. the 48-hour free premium
// every new signup gets). Counts down live and links to /subscribe so the
// switch to paid is never a surprise. Hidden for real (paid) premium accounts
// and anyone without an active grant. Wording is admin-editable (Premium
// Preview tool) via /premium-preview/display-config — {time} is the countdown.
const fmt = (ms) => {
  if (ms <= 0) return "0s";
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
};

const DEFAULTS = {
  prefix: "Premium unlocked —",
  suffix: "left. Subscribe to keep it after your free access ends.",
  cta: "Subscribe",
};

export default function PreviewSelfCountdownBanner() {
  const { user } = useApp();
  const navigate = useNavigate();
  const [expiresAt, setExpiresAt] = useState(null);
  const [copy, setCopy] = useState(DEFAULTS);
  const [now, setNow] = useState(Date.now());

  const poll = useCallback(async () => {
    const learnerId = getActiveLearnerId(user?.id);
    if (!learnerId) { setExpiresAt(null); return; }
    try {
      // A site-wide "everyone" window is shown by the global PremiumPreviewCountdown
      // banner, so defer to it and avoid a double banner. Only show this personal
      // banner for a per-account grant (e.g. the signup window) when no global
      // window is running.
      const [res, glob] = await Promise.all([
        axios.get(`${API}/entitlements/resolve/${encodeURIComponent(learnerId)}`),
        axios.get(`${API}/premium-preview/active`).catch(() => ({ data: {} })),
      ]);
      const data = res.data;
      if (glob?.data?.active) { setExpiresAt(null); return; }
      setExpiresAt(data?.preview && data?.preview_expires_at ? data.preview_expires_at : null);
    } catch (_) { setExpiresAt(null); }
  }, [user?.id]);

  useEffect(() => {
    // Editable banner wording (falls back to sensible defaults).
    axios.get(`${API}/premium-preview/display-config`)
      .then((r) => {
        const bc = r.data?.banner_copy || {};
        setCopy({
          prefix: (bc.prefix || "").trim() || DEFAULTS.prefix,
          suffix: (bc.suffix || "").trim() || DEFAULTS.suffix,
          cta: (bc.cta || "").trim() || DEFAULTS.cta,
        });
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!user?.id) { setExpiresAt(null); return; }
    poll();
    const p = setInterval(poll, 60000);
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => { clearInterval(p); clearInterval(t); };
  }, [poll, user?.id]);

  if (!user?.id || !expiresAt) return null;
  const remaining = new Date(expiresAt).getTime() - now;
  if (remaining <= 0) return null;

  return (
    <button
      type="button"
      onClick={() => navigate("/subscribe")}
      data-testid="preview-self-countdown"
      className="w-full bg-gradient-to-r from-amber-500 via-orange-500 to-fuchsia-600 text-white py-1.5 px-3 text-xs sm:text-sm font-bold flex items-center justify-center gap-2 shadow hover:brightness-110 transition-all"
    >
      <Sparkles size={14} className="text-amber-100 shrink-0" />
      <span>
        {copy.prefix} <span data-testid="preview-self-time" className="tabular-nums text-amber-100">{fmt(remaining)}</span> {copy.suffix}
      </span>
      <span className="inline-flex items-center gap-0.5 underline underline-offset-2 shrink-0" data-testid="preview-self-subscribe">
        {copy.cta} <ChevronRight size={13} />
      </span>
    </button>
  );
}
